import AppsContainer from '@nxsys/components/AppsContainer';
import InvoiceForm, {
  InvoiceFormValues,
  InvoiceFormRef,
} from '../components/InvoiceForm';
import { useCallback, useMemo, useRef } from 'react';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Space } from 'antd';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import { useCreateInvoice } from '@nxsys/hooks/api/invoice/useInvoiceApi';
import { CreateInvoiceParams } from '@nxsys/types/api';
import moment from 'moment';
import { makeCode } from '@nxsys/utils';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { showMessage } from '@nxsys/utils/message';

const CreateInvoice = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const { run, loading } = useCreateInvoice();
  const invNumberDefault = useMemo(() => makeCode('SON', 10), []);
  const { isBureau } = useSelector((state: AppState) => state.appStore);

  const formRef = useRef<InvoiceFormRef>(null);

  const handleGoBack = () => navigate('/invoices');

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <div className="flex items-center gap-x-3">
          <AppPageTitle
            separator=""
            prefix={messages['invoices.addNewInvoice']}
            title=""
          />
        </div>
      </div>
    );
  }, [messages]);

  const renderRightContent = useCallback(() => {
    return (
      <div className="flex items-center justify-between">
        <Space>
          <AppButton
            variant="outlined"
            type="primary"
            name={messages['common.cancel']}
            onClick={handleGoBack}
          />
          <AppButton
            type="primary"
            name={messages['common.save']}
            onClick={() => {
              formRef.current?.submit();
            }}
            loading={loading}
            disabled={loading}
          />
        </Space>
      </div>
    );
  }, [messages, formRef, loading]);

  const handleSubmit = (values: InvoiceFormValues) => {
    let params: CreateInvoiceParams = {
      companyRef: values.companyRef?.value ?? '',
      endDate: moment(values.endDate).toISOString(),
      invoicedDate: moment(values.invoicedDate).toISOString(),
      reference: values.reference ?? '',
      rates:
        values.rates?.map((rate) => ({
          ...rate,
          rateCode: rate.rateCode?.value ?? '',
          vatDetails: rate.vatDetails?.value ?? '',
          includeInCisCalculation: true,
        })) ?? [],
      invoiceNo: `INV-${invNumberDefault}`,
    };
    if (!isBureau) {
      params = {
        ...params,
        agencyRef: values.agencyRef?.value ?? '',
      };
    }
    run(params)
      .then(() => {
        showMessage({
          type: 'success',
          content: messages['invoices.createInvoiceSuccess'],
        });
        handleGoBack();
      })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={loading}
    >
      <InvoiceForm formRef={formRef} onSubmit={handleSubmit} />
    </AppsContainer>
  );
};

export default CreateInvoice;
